import { ApolloClient } from '@apollo/client';
import { AssetAvailabilityReason } from '@see-power-pft-common-js/availability/asset-availability/enums/asset-availability-reason';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import { addDays, differenceInDays } from 'date-fns';
import {
  NewAssetAvailabilityEntry,
  NewSiteAvailabilityEntry,
} from 'pages/Availability/common/FormDrawer/types';
import {
  PRODUCE_ASSET_AVAILABILITIES,
  PRODUCE_ASSET_AVAILABILITIES_UPDATE,
  PRODUCE_SITE_AVAILABILITIES,
  PRODUCE_SITE_AVAILABILITIES_UPDATE,
} from 'pages/Availability/mutations';
import { areEqualUpdatableContentProperties } from 'pages/Availability/no-changes';
import {
  Asset,
  AssetAvailability,
  ProduceAssetAvailabilityInputItem,
  ProduceAssetAvailabilityUpdateInputItem,
  ProduceSiteAvailabilityItemDto,
  ProduceSiteAvailabilityUpdateItemDto,
  SiteAvailability,
} from 'pages/Availability/types';
import { ApiName } from 'types/apiName';

// Parse form values for creating new availability entries (handles repeat logic)
export function parseFormValuesForSiteAvailabilityMutation(
  values: NewSiteAvailabilityEntry,
  siteSourceKey: string,
): ProduceSiteAvailabilityItemDto[] {
  const startDate = new Date(values.start!);
  const endDate = new Date(values.end!);

  const n =
    values.repeatDaily && values.repeatEndInclusive
      ? differenceInDays(new Date(values.repeatEndInclusive), startDate) + 2
      : 1;

  const items = Array.from({ length: n }).map((_, i) => {
    // Offset start/end by i days, preserve time
    const start = addDays(startDate, i);
    const end = addDays(endDate, i);

    const item: ProduceSiteAvailabilityItemDto = {
      refSiteSourceKey: siteSourceKey,
      start,
      end,
      maxExportPower: values.maxExportPower
        ? +values.maxExportPower
        : undefined,
      maxImportPower: values.maxImportPower
        ? +values.maxImportPower
        : undefined,
      energyCapacity: values.energyCapacity
        ? +values.energyCapacity
        : undefined,
      // Temporary for now
      siteAvailabilityReason: values.availabilityReason!,
      notes: values.notes ?? undefined,
    };

    return item;
  });

  return items;
}

// Parse form values for updating existing availability entries
export function parseFormValuesForSiteAvailabilityUpdateMutation(
  values: NewSiteAvailabilityEntry,
  existingSiteAvailability: SiteAvailability,
): ProduceSiteAvailabilityUpdateItemDto[] {
  if (!values.start || !values.end) {
    return [];
  }

  const item: ProduceSiteAvailabilityUpdateItemDto = {
    sourceName: existingSiteAvailability.sourceName,
    sourceId: existingSiteAvailability.sourceId,
    start: new Date(values.start),
    end: new Date(values.end),
    notes: values.notes ?? undefined,
    isCancelled: existingSiteAvailability.isCancelled,
    lastUpdatedByUserAt: new Date(existingSiteAvailability.updatedByUserAt!),
  };

  return [item];
}

// Mutation-specific function for creating new entries
export async function performNewSiteAvailabilityMutation(
  client: ApolloClient,
  values: NewSiteAvailabilityEntry,
  siteSourceKey: string,
): Promise<{ success: boolean; count: number; message: string }> {
  try {
    const items = parseFormValuesForSiteAvailabilityMutation(
      values,
      siteSourceKey,
    );

    const response = await client.mutate({
      mutation: PRODUCE_SITE_AVAILABILITIES,
      variables: { input: { items } },
      context: { apiName: ApiName.plutoAvailabilityApi },
    });
    if (response.error) {
      throw response.error;
    }

    const count = items.length;
    const message = `Availability entr${count > 1 ? 'ies' : 'y'} created successfully`;
    return { success: true, count, message };
  } catch (error) {
    const message = `Failed to create availability entr${values.repeatDaily ? 'ies' : 'y'}`;
    return { success: false, count: 0, message };
  }
}

// Mutation-specific function for editing existing entries
export async function performEditSiteAvailabilityMutation(
  client: ApolloClient,
  values: NewSiteAvailabilityEntry,
  existingSiteAvailability: SiteAvailability,
): Promise<{ success: boolean; hasChanges: boolean; message: string }> {
  try {
    const items = parseFormValuesForSiteAvailabilityUpdateMutation(
      values,
      existingSiteAvailability,
    );

    // Check for changes
    const existing = {
      start: new Date(existingSiteAvailability.start),
      end: existingSiteAvailability.end
        ? new Date(existingSiteAvailability.end)
        : undefined,
      notes: existingSiteAvailability.notes ?? null,
      isCancelled: existingSiteAvailability.isCancelled,
    };

    const filteredItems = items.filter((item) => {
      return !areEqualUpdatableContentProperties(item, existing);
    });

    if (filteredItems.length === 0) {
      return {
        success: true,
        hasChanges: false,
        message: 'No changes detected',
      };
    }

    const response = await client.mutate({
      mutation: PRODUCE_SITE_AVAILABILITIES_UPDATE,
      variables: { input: { items: filteredItems } },
      context: { apiName: ApiName.plutoAvailabilityApi },
    });
    if (response.error) {
      throw response.error;
    }

    const message = `Availability entry ${existingSiteAvailability.id} updated successfully`;
    return { success: true, hasChanges: true, message };
  } catch (error) {
    return {
      success: false,
      hasChanges: true,
      message: 'Failed to update availability entry',
    };
  }
}

// ============ Asset Availability Handlers ============

// Parse form values for creating new asset availability entries (handles repeat logic)
function parseFormValuesForAssetAvailabilityMutation(
  values: NewAssetAvailabilityEntry,
  assets: Asset[],
): Map<Asset['name'], ProduceAssetAvailabilityInputItem[]> {
  const startDate = new Date(values.start!);
  const endDate = new Date(values.end!);

  const n =
    values.repeatDaily && values.repeatEndInclusive
      ? differenceInDays(new Date(values.repeatEndInclusive), startDate) + 2
      : 1;

  const items: Map<Asset['name'], ProduceAssetAvailabilityInputItem[]> =
    new Map();

  // For each day (if repeating) and each selected asset, create an entry
  for (let i = 0; i < n; i++) {
    const start = addDays(startDate, i);
    const end = addDays(endDate, i);

    values.selectedAssets.forEach((assetSourceKey) => {
      const asset = assets.find((a) => getSourceKey(a) === assetSourceKey);
      if (!asset) return;

      const inputs = values.assetInputs[assetSourceKey];

      const item: ProduceAssetAvailabilityInputItem = {
        refAssetSourceKey: assetSourceKey,
        start,
        end,
        maxExportPower: inputs?.maxExportPower
          ? +inputs.maxExportPower
          : undefined,
        maxImportPower: inputs?.maxImportPower
          ? +inputs.maxImportPower
          : undefined,
        energyCapacity: inputs?.energyCapacity
          ? +inputs.energyCapacity
          : undefined,
        assetAvailabilityReason:
          values.availabilityReason as AssetAvailabilityReason,
        notes: values.notes ?? undefined,
      };

      (
        items.get(asset.name) ?? items.set(asset.name, []).get(asset.name)!
      ).push(item);
    });
  }

  return items;
}

// Mutation-specific function for creating new asset availability entries
export async function performNewAssetAvailabilityMutation(
  client: ApolloClient,
  values: NewAssetAvailabilityEntry,
  assets: Asset[],
): Promise<{ success: boolean; count: number; message: string }> {
  try {
    const items = parseFormValuesForAssetAvailabilityMutation(values, assets);

    for (const assetItems of Array.from(items.values())) {
      const response = await client.mutate({
        mutation: PRODUCE_ASSET_AVAILABILITIES,
        variables: { input: { items: assetItems } },
        context: { apiName: ApiName.plutoAvailabilityApi },
      });
      if (response.error) {
        throw response.error;
      }
    }

    const count = Array.from(items.values()).reduce(
      (acc, assetItems) => acc + assetItems.length,
      0,
    );
    const message = `Availability entr${count > 1 ? 'ies' : 'y'} created successfully`;
    return { success: true, count, message };
  } catch (error) {
    const message = `Failed to create availability entr${values.selectedAssets.length > 1 || values.repeatDaily ? 'ies' : 'y'}`;
    return { success: false, count: 0, message };
  }
}

// Parse form values for updating existing asset availability entries
export function parseFormValuesForAssetAvailabilityUpdateMutation(
  values: NewAssetAvailabilityEntry,
  existingAssetAvailability: AssetAvailability,
): ProduceAssetAvailabilityUpdateInputItem[] {
  if (!values.start || !values.end) {
    return [];
  }

  const item: ProduceAssetAvailabilityUpdateInputItem = {
    sourceName: existingAssetAvailability.sourceName,
    sourceId: existingAssetAvailability.sourceId,
    start: new Date(values.start),
    end: new Date(values.end),
    notes: values.notes ?? undefined,
    isCancelled: existingAssetAvailability.isCancelled ?? false,
    lastUpdatedByUserAt: new Date(existingAssetAvailability.updatedByUserAt!),
  };

  return [item];
}

// Mutation-specific function for editing asset availability entries
export async function performEditAssetAvailabilityMutation(
  client: ApolloClient,
  values: NewAssetAvailabilityEntry,
  existingAssetAvailability: AssetAvailability,
): Promise<{ success: boolean; hasChanges: boolean; message: string }> {
  try {
    const items = parseFormValuesForAssetAvailabilityUpdateMutation(
      values,
      existingAssetAvailability,
    );

    // Check for changes
    const existing = {
      start: new Date(existingAssetAvailability.start),
      end: existingAssetAvailability.end
        ? new Date(existingAssetAvailability.end)
        : undefined,
      maxImportPower: existingAssetAvailability.maxImportPower ?? undefined,
      maxExportPower: existingAssetAvailability.maxExportPower ?? undefined,
      energyCapacity: existingAssetAvailability.energyCapacity ?? undefined,
      notes: existingAssetAvailability.notes ?? null,
      isCancelled: existingAssetAvailability.isCancelled,
    };

    const filteredItems = items.filter((item) => {
      return !areEqualUpdatableContentProperties(item, existing);
    });

    if (filteredItems.length === 0) {
      return {
        success: true,
        hasChanges: false,
        message: 'No changes detected',
      };
    }

    const response = await client.mutate({
      mutation: PRODUCE_ASSET_AVAILABILITIES_UPDATE,
      variables: { input: { items: filteredItems } },
      context: { apiName: ApiName.plutoAvailabilityApi },
    });
    if (response.error) {
      throw response.error;
    }

    const message = `Availability entry ${existingAssetAvailability.id} updated successfully`;
    return { success: true, hasChanges: true, message };
  } catch (error) {
    return {
      success: false,
      hasChanges: true,
      message: 'Failed to update availability entry',
    };
  }
}
